import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import Layout from '../../components/layout/main';
import {
    Button,
    Fade,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import AssessmentIcon from '@mui/icons-material/Assessment';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { AuthContext } from '../../context/auth';
import { getData, getDataFull, downloadGet } from '../../services/ajax';
import { useSnackbar } from 'notistack';
import ListadoOperativoBanner from '../../components/listados/ListadoOperativoBanner';
import ProcessGuide from '../../components/orden/ProcessGuide';
import { ChartManagerV2 } from '../../components/chart-v2';
import dayjs from 'dayjs';

const WINDOW_CHIPS = [30, 60, 90, 180, 365];

function formatPct(v) {
    if (v == null) return '—';
    return `${(Number(v) * 100).toFixed(1)} %`;
}

function formatSeg(v) {
    if (v == null) return '—';
    const s = Number(v);
    if (s < 60) return `${s} s`;
    if (s < 3600) return `${Math.round(s / 60)} min`;
    return `${(s / 3600).toFixed(1)} h`;
}

function Page() {
    const { usuario } = useContext(AuthContext);
    const { enqueueSnackbar } = useSnackbar();
    const [ventana, setVentana] = useState(30);
    const [resumen, setResumen] = useState(null);
    const [porEst, setPorEst] = useState([]);
    const [porPrac, setPorPrac] = useState([]);
    const [series, setSeries] = useState([]);

    const rango = useMemo(() => {
        const hasta = dayjs().format('YYYY-MM-DD');
        const desde = dayjs().subtract(ventana, 'day').format('YYYY-MM-DD');
        return { desde, hasta };
    }, [ventana]);

    const load = useCallback(async () => {
        const qs = `desde=${rango.desde}&hasta=${rango.hasta}`;
        const [r, e, p, s] = await Promise.all([
            getData(`auditoria/resumen?${qs}`, usuario.token),
            getDataFull('auditoria/por-establecimiento', usuario.token),
            getDataFull('auditoria/por-practica', usuario.token),
            getDataFull(`auditoria/series?${qs}&agrupacion=dia`, usuario.token),
        ]);
        if (r.error) enqueueSnackbar(r.error, { variant: 'error' });
        else setResumen(r.data || {});
        if (!e.error) setPorEst(Array.isArray(e.data) ? e.data : []);
        if (!p.error) setPorPrac(Array.isArray(p.data) ? p.data : []);
        if (!s.error) setSeries(Array.isArray(s.data) ? s.data : []);
    }, [rango, usuario.token, enqueueSnackbar]);

    useEffect(() => { load(); }, [load]);

    const kpis = useMemo(() => {
        const src = resumen?.efectivizadas != null ? resumen : (resumen?.data || {});
        return [
            { label: 'Efectivizadas', value: src.efectivizadas ?? 0 },
            { label: 'Con turno', value: src.con_turno ?? 0 },
            { label: 'Pendiente manual', value: src.pendiente_manual ?? 0 },
            { label: 'Demora promedio', value: formatSeg(src.demora_promedio_segundos) },
            { label: 'Tasa timeout', value: formatPct(src.tasa_timeout) },
            { label: 'Tasa rechazo', value: formatPct(src.tasa_rechazo) },
            { label: 'Turnos cancelados', value: src.turnos_cancelados ?? 0 },
        ];
    }, [resumen]);

    const chartData = useMemo(() => {
        if (!series.length) return { data: [], agrupaciones: [] };
        return {
            agrupaciones: series.map((s) => s.periodo),
            data: [
                { id: 1, label: 'Creadas', color: 0, data: series.map((s) => s.creadas) },
                { id: 2, label: 'Turnos', color: 1, data: series.map((s) => s.turnos) },
                { id: 3, label: 'Cierres', color: 2, data: series.map((s) => s.cierres) },
                { id: 4, label: 'Timeouts', color: 3, data: series.map((s) => s.timeouts) },
            ],
        };
    }, [series]);

    const onExport = async () => {
        const qs = `desde=${rango.desde}&hasta=${rango.hasta}`;
        const result = await downloadGet(`auditoria/export?${qs}`, usuario.token, 'auditoria_establecimientos.csv');
        if (result.error) enqueueSnackbar(result.error, { variant: 'error' });
        else enqueueSnackbar('Exportación iniciada', { variant: 'success' });
    };

    return (
        <Layout nombrePagina="Auditoría">
            <Fade in timeout={900}>
                <Grid size={12}>
                    <ListadoOperativoBanner
                        title="Auditoría de órdenes"
                        description="Indicadores de efectividad, demoras y timeouts por ventana temporal. Permite exportar el detalle por establecimiento."
                        icon={AssessmentIcon}
                        accentColor="info"
                        headerAction={(
                            <Button startIcon={<FileDownloadIcon />} onClick={onExport}>
                                Exportar CSV
                            </Button>
                        )}
                        chips={WINDOW_CHIPS.map((d) => ({
                            key: String(d),
                            label: `${d} días`,
                            color: 'info',
                        }))}
                        selectedChip={String(ventana)}
                        onChipChange={(key) => setVentana(Number(key))}
                    />

                    <ProcessGuide
                        title="Lectura de indicadores"
                        steps={[
                            'Elija la ventana de análisis (30 a 365 días).',
                            'Revise los KPI y las series temporales del período.',
                            'Exporte el CSV para análisis externo por establecimiento.',
                        ]}
                    />

                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        {kpis.map((k) => (
                            <Grid key={k.label} size={{ xs: 6, sm: 4, md: 2 }}>
                                <Paper sx={{ p: 2, textAlign: 'center', height: '100%' }} elevation={1}>
                                    <Typography variant="h5" sx={{ fontWeight: 800, color: 'primary.main' }}>
                                        {k.value}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">{k.label}</Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>

                    <Paper sx={{ p: 2, mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Series del período</Typography>
                        {chartData.data.length ? (
                            <ChartManagerV2
                                titulo="Actividad"
                                data={chartData.data}
                                agrupaciones={chartData.agrupaciones}
                                mostrarBarra
                                mostrarTorta={false}
                            />
                        ) : (
                            <Typography variant="body2" color="text.secondary">Sin datos en el período.</Typography>
                        )}
                    </Paper>

                    <Paper sx={{ p: 2, mb: 2, overflow: 'auto' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Por establecimiento</Typography>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Establecimiento</TableCell>
                                    <TableCell>Tipo</TableCell>
                                    {WINDOW_CHIPS.map((d) => <TableCell key={d} align="right">V{d}</TableCell>)}
                                    <TableCell align="right">Timeout</TableCell>
                                    <TableCell align="right">Demora</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {porEst.map((row) => (
                                    <TableRow key={row.establecimiento_id}>
                                        <TableCell>{row.nombre}</TableCell>
                                        <TableCell>{row.tipo}</TableCell>
                                        {WINDOW_CHIPS.map((d) => (
                                            <TableCell key={d} align="right">{row.ventanas?.[d] ?? 0}</TableCell>
                                        ))}
                                        <TableCell align="right">{formatPct(row.tasa_timeout)}</TableCell>
                                        <TableCell align="right">{formatSeg(row.demora_respuesta_promedio)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Paper>

                    <Paper sx={{ p: 2, overflow: 'auto' }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Por práctica</Typography>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Práctica</TableCell>
                                    {WINDOW_CHIPS.map((d) => <TableCell key={d} align="right">V{d}</TableCell>)}
                                    <TableCell align="right">Timeout</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {porPrac.map((row) => (
                                    <TableRow key={row.practica_id}>
                                        <TableCell>{row.nombre}</TableCell>
                                        {WINDOW_CHIPS.map((d) => (
                                            <TableCell key={d} align="right">{row.ventanas?.[d] ?? 0}</TableCell>
                                        ))}
                                        <TableCell align="right">{formatPct(row.tasa_timeout)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Paper>
                </Grid>
            </Fade>
        </Layout>
    );
}

export default Page;
